import React, { type ChangeEvent, type CSSProperties, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import BaseButton from "../../components/BaseButton/BaseButton";
import { useNavigationHeader } from "../../components/MainLayout";
import { useAppData } from "../../data/AppDataProvider";
import styles from "./Tariffs.module.css";

const sliderSteps = [1, 2, 3, 4, 5];
const payButtonFullLabel = "Выбрать способ оплаты";
const payButtonShortLabel = "Выбрать способ оп...";

type MonthKey = "1m" | "3m" | "6m" | "12m";

type MonthOption = {
    key: MonthKey;
    label: string;
    months: number;
};

const monthsList: MonthOption[] = [
    { key: "1m", label: "1 месяц", months: 1 },
    { key: "3m", label: "3 месяца", months: 3 },
    { key: "6m", label: "6 месяцев", months: 6 },
    { key: "12m", label: "1 год", months: 12 },
];

type OrderType = "change_devices" | "tariff_pay";

type Tariff = {
    price?: number | string | null;
    per_month?: number | string | null;
    discount?: number | string | null;
};

type TariffsData = {
    curr_devices?: number | string | null;
    tarifs?: Record<string, Partial<Record<MonthKey, Tariff>>>;
};

type TariffsStatus = {
    isLoading: boolean;
    error?: string;
};

type AppData = {
    tariffsData: TariffsData;
    tariffsStatus: TariffsStatus;
};

type TariffsLocationState = {
    orderType?: OrderType;
} | null;

type AppearStyle = CSSProperties & {
    "--appear-delay": string;
};

type NavigationHeaderData = {
    shouldShowNavigation: boolean;
    setBottomActions: (actions: React.ReactNode | null) => void;
};

function getAppearStyle(): AppearStyle {
    return {
        "--appear-delay": `${(0.08 + Math.random() * 0.18).toFixed(3)}s`,
    };
}

function formatDiscount(discount: number) {
    if (!Number.isFinite(discount) || discount <= 0) {
        return null;
    }

    return `- ${discount}%`;
}

function formatMoneyValue(value: number) {
    const roundedValue = Math.ceil(value);

    if (!Number.isFinite(roundedValue)) {
        return "...";
    }

    return roundedValue.toLocaleString("ru-RU");
}

function formatMonthlyCalculation(tariff: Tariff | undefined, devices: number, months: number) {
    const explicitPerMonth = Number(tariff?.per_month);
    const price = Number(tariff?.price);
    const perMonth = Number.isFinite(explicitPerMonth) && explicitPerMonth > 0
        ? explicitPerMonth
        : price / months;

    if (!Number.isFinite(perMonth) || perMonth <= 0) {
        return null;
    }

    if (devices > 1) {
        return `${devices}×${formatMoneyValue(perMonth / devices)} ₽ в месяц`;
    }

    return `${formatMoneyValue(perMonth)} ₽ в месяц`;
}

function PayButtonLabel() {
    const [shouldShortenPayLabel, setShouldShortenPayLabel] = useState(false);
    const payLabelRef = useRef<HTMLElement | null>(null);
    const payLabelMeasureRef = useRef<HTMLSpanElement | null>(null);

    useLayoutEffect(() => {
        const payLabel = payLabelRef.current;
        const payLabelMeasure = payLabelMeasureRef.current;

        if (!payLabel || !payLabelMeasure) {
            return undefined;
        }

        const updatePayLabel = () => {
            const nextShouldShorten = payLabelMeasure.scrollWidth > payLabel.clientWidth;
            setShouldShortenPayLabel((current) => (
                current === nextShouldShorten ? current : nextShouldShorten
            ));
        };

        updatePayLabel();

        if (typeof ResizeObserver === "undefined") {
            window.addEventListener("resize", updatePayLabel);
            return () => {
                window.removeEventListener("resize", updatePayLabel);
            };
        }

        const resizeObserver = new ResizeObserver(updatePayLabel);
        resizeObserver.observe(payLabel);

        return () => {
            resizeObserver.disconnect();
        };
    }, []);

    return (
        <b className={styles.payLabel} ref={payLabelRef}>
            <span className={styles.payLabelText}>
                {shouldShortenPayLabel ? payButtonShortLabel : payButtonFullLabel}
            </span>
            <span className={styles.payLabelMeasure} ref={payLabelMeasureRef} aria-hidden="true">
                {payButtonFullLabel}
            </span>
        </b>
    );
}

function Tariffs() {
    const location = useLocation();
    const navigate = useNavigate();
    const { shouldShowNavigation, setBottomActions } = useNavigationHeader() as NavigationHeaderData;
    const { tariffsData, tariffsStatus } = useAppData() as AppData;
    const { isLoading, error } = tariffsStatus;
    const locationState = location.state as TariffsLocationState;
    const orderType: OrderType = locationState?.orderType === "change_devices" ? "change_devices" : "tariff_pay";
    const currentDevices = Number(tariffsData.curr_devices) || 1;
    const minDevices = orderType === "change_devices" ? currentDevices + 1 : 1;
    const [devices, setDevices] = useState(1);
    const [selectedMonth, setSelectedMonth] = useState<MonthKey | null>(null);

    useEffect(() => {
        setSelectedMonth(null);
        setDevices(orderType === "change_devices" ? minDevices : currentDevices);
    }, [currentDevices, minDevices, orderType]);

    const availableSteps = useMemo(
        () => sliderSteps.filter((step) => step >= minDevices),
        [minDevices],
    );
    const activeStepIndex = Math.max(0, availableSteps.indexOf(devices));
    const activeStepPosition = availableSteps.length > 1
        ? (activeStepIndex / (availableSteps.length - 1)) * 100
        : 0;
    const currentTariffs = tariffsData.tarifs?.[String(devices)] ?? {};
    const selectedMonthData = monthsList.find((month) => month.key === selectedMonth) || null;
    const selectedTariff = selectedMonth ? currentTariffs?.[selectedMonth] : null;
    const price = selectedTariff?.price ?? null;
    const hasTariffs = monthsList.some((month) => currentTariffs?.[month.key]?.price != null);
    const isChangeDevicesFlowBlocked = orderType === "change_devices" && availableSteps.length === 0;
    const payState = useMemo(() => selectedMonthData && selectedTariff ? {
        devices,
        monthsKey: selectedMonthData.key,
        monthsCount: selectedMonthData.months,
        monthsLabel: selectedMonthData.label,
        price: selectedTariff.price,
        perMonth: selectedTariff.per_month,
        discount: selectedTariff.discount,
        orderType,
    } : null, [devices, orderType, selectedMonthData, selectedTariff]);

    const handleDevicesChange = (event: ChangeEvent<HTMLInputElement>) => {
        setDevices(Number(event.target.value));
        setSelectedMonth(null);

        if (typeof navigator !== "undefined" && typeof navigator.vibrate === "function") {
            navigator.vibrate(15);
        }
    };

    const isPayDisabled = !selectedMonth || !price || isChangeDevicesFlowBlocked;
    const payAction = useMemo(() => (
        <BaseButton
            icon="card.svg"
            type="button"
            disabled={isPayDisabled}
            className={`app-bottom-nav-btn ${styles.payBtn}`}
            onClick={() => navigate("/paytype", { state: payState })}
        >
            <span className={styles.payContent}>
                <PayButtonLabel />
                {price ? (
                    <span className={styles.payAmount}>
                        <span id="price_btn_text" className={styles.payPrice}>{price}</span>
                        <span className={styles.payCurrency}>
                            ₽
                        </span>
                    </span>
                ) : null}
            </span>
        </BaseButton>
    ), [isPayDisabled, navigate, payState, price]);

    useEffect(() => {
        if (!shouldShowNavigation) {
            setBottomActions(null);
            return undefined;
        }

        setBottomActions(payAction);

        return () => {
            setBottomActions(null);
        };
    }, [payAction, setBottomActions, shouldShowNavigation]);

    return (
        <>
            <div className={styles.headerBlock}>
                <h2 className="page-header-title">Тарифы</h2>
                <span className={styles.headerSubtitle}>Выберите оптимальный для вас тариф</span>
            </div>
            {error ? (
                <div className="gray-block pad1 shadow-1 menu-appear" style={getAppearStyle()}>
                    <span>{error}</span>
                </div>
            ) : null}

            <div className="flex-col gap05 menu-appear" style={getAppearStyle()}>
                {orderType === "change_devices" ? (
                    <p className="grey">Этот заказ будет создан как перерасчет за смену количества устройств.</p>
                ) : null}
                <p className={styles.devicesCountText}>
                    Количество устройств: <b className={styles.devicesCountValue}>{devices}</b>
                </p>
                <div className={`${styles.sliderWrap} menu-appear`} style={getAppearStyle()}>
                    <div className={styles.sliderScale} aria-hidden="true">
                        <div className={styles.sliderMarks}>
                            {availableSteps.map((num, index) => (
                                <span
                                    key={num}
                                    className={`${styles.sliderMark} ${devices === num ? styles.sliderMarkActive : ""}`}
                                    style={{ left: `${availableSteps.length > 1 ? (index / (availableSteps.length - 1)) * 100 : 0}%` }}
                                />
                            ))}
                        </div>
                        <span
                            className={styles.sliderThumb}
                            style={{ left: `${activeStepPosition}%` }}
                        />
                        <div className={styles.sliderLabels}>
                            {availableSteps.map((num, index) => (
                                <span
                                    key={num}
                                    className={`${styles.sliderLabel} ${devices === num ? styles.sliderLabelActive : ""}`}
                                    style={{
                                        left: `${availableSteps.length > 1 ? (index / (availableSteps.length - 1)) * 100 : 0}%`,
                                    }}
                                >
                                    {num}
                                </span>
                            ))}
                        </div>
                    </div>
                    <input
                        type="range"
                        min={availableSteps[0] ?? 1}
                        max={availableSteps[availableSteps.length - 1] ?? 1}
                        step="1"
                        value={devices}
                        onChange={handleDevicesChange}
                        className={styles.slider}
                        aria-label="Количество устройств"
                        disabled={isLoading || !hasTariffs || isChangeDevicesFlowBlocked}
                    />
                </div>
            </div>

            {isChangeDevicesFlowBlocked ? (
                <div className="gray-block pad1 shadow-1 menu-appear" style={getAppearStyle()}>
                    <span>У вас уже максимум устройств в тарифе.</span>
                </div>
            ) : null}

            <div className={`${styles.tariffsGrid} tarifs-grid gap1`}>
                {monthsList.map((month) => {
                    const monthTariff = currentTariffs?.[month.key];
                    const discountLabel = formatDiscount(Number(monthTariff?.discount));
                    const monthlyCalculation = formatMonthlyCalculation(monthTariff, devices, month.months);
                    const isPopular = devices === 1 && month.key === "1m" && orderType === "tariff_pay";

                    return (
                        <button
                            key={month.key}
                            type="button"
                            className={`${styles.tariffButton} pad1 flex-col flex-center w100 menu-appear ${
                                selectedMonth === month.key ? styles.tariffButtonSelected : ""
                            }`}
                            onClick={() => setSelectedMonth(month.key)}
                            data-selected={selectedMonth === month.key}
                            disabled={!monthTariff?.price || isChangeDevicesFlowBlocked}
                            style={getAppearStyle()}
                        >
                            <div className={styles.tariffContent}>
                                <span className={styles.tariffIconFrame} aria-hidden="true">
                                    <svg className={styles.tariffIcon}>
                                        <use href="/svg/calendar.svg#calendar"></use>
                                    </svg>
                                </span>
                                <div className={styles.tariffText}>
                                    <span className={styles.tariffLabel}>{month.label}</span>
                                    <div className={styles.tariffPrice}>
                                        <span className={styles.tariffPriceValue}>{monthTariff?.price ?? "..."}</span>
                                        <span className={styles.tariffCurrency}>₽</span>
                                    </div>
                                    {monthlyCalculation ? (
                                        <span className={styles.tariffMonthly}>{monthlyCalculation}</span>
                                    ) : null}
                                </div>
                                {isPopular && (
                                    <div className={styles.popularBadge}>
                                        популярное
                                    </div>
                                )}
                                {discountLabel && (
                                    <div className={styles.discountBadge}>
                                        {discountLabel}
                                    </div>
                                )}
                            </div>
                        </button>
                    );
                })}
            </div>
        </>
    );
}

export default Tariffs;
